import { bool, func, node, oneOf, string } from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";

import styled from "styled-components";
import { cursor } from "theme";
import { SMALL_POPOVER_WIDTH } from "theme/constants";

import FnrTypography from "FnrTypography";

import CircleCheck from "UI/FnrEnrichedCheckbox/CircleCheck";
import FnrLoadingBricks from "UI/FnrLoadingBricks";
import FnrSlides from "UI/FnrSlides";
import FnrHorizontalGap from "UI/commonContainers/FnrHorizontalGap";
import FnrVerticalGap from "UI/commonContainers/FnrVerticalGap";

import externalNavigate from "Utilities/externalNavigate";

const SFnrTypography = styled(FnrTypography)`
  ${cursor}
`;

const SFnrVerticalGap = styled(FnrVerticalGap)`
  ${({ $fullWidth }) =>
    $fullWidth ? "" : `max-width: ${SMALL_POPOVER_WIDTH}px;`};
`;

/**
 *
 * @param {object} param0
 * @param {string} url - the url to redirect the user to.
 * @param {"_blank"|"_self"} target - the target prop passed to window.open
 * @param {function} callback - the function called after pretend loading finishes. Can be used in place of a url.
 * @returns
 */
const FnrRedirect = ({
  url,
  target = "_blank",
  callback,
  children,
  fullWidth,
  ...rest
}) => {
  const [isChecked, setIsChecked] = useState(false);
  const [isNavigating, setIsNavigating] = useState(true);
  const [hasQueuedNavigation, setHasQueuedNavigation] = useState(false);

  const handleNavigate = useCallback(() => {
    if (url) externalNavigate(url, target);
    if (callback) callback();
  }, [url, target, callback]);

  useEffect(() => {
    setTimeout(() => {
      setIsChecked(true);
    }, 250);
  }, []);

  // TODO: handleNavigate is called twice. Make that...not a thing.
  useEffect(() => {
    if (!hasQueuedNavigation) {
      setTimeout(() => {
        setIsNavigating(false);
        handleNavigate();
      }, 1000);
    }
    setHasQueuedNavigation(true);
  }, [handleNavigate, hasQueuedNavigation]);

  const activeKey = useMemo(() => {
    if (isNavigating) return "loading";
    if (url && !isNavigating) return "manual";
    if (!url && !isNavigating) return "static";
  }, [isNavigating, url]);

  return (
    <SFnrVerticalGap
      data-cy="FnrRedirect_Container"
      $fullWidth={fullWidth}
      {...rest}
    >
      <FnrHorizontalGap>
        <div>
          <CircleCheck checked={isChecked} />
        </div>
        {children}
      </FnrHorizontalGap>
      <FnrHorizontalGap justifyContent="center">
        <FnrSlides
          clip={false}
          activeKey={activeKey}
          components={[
            { key: "loading", value: () => <FnrLoadingBricks show /> },
            {
              key: "manual",
              value: () => (
                <SFnrTypography
                  type="copy"
                  color="primary"
                  fontSize="md"
                  onClick={handleNavigate}
                  data-cy="FnrRedirect_ManualRedirect"
                  noWrap
                >
                  Click here if not automatically redirected.
                </SFnrTypography>
              ),
            },
            { key: "static", value: () => null },
          ]}
        />
      </FnrHorizontalGap>
    </SFnrVerticalGap>
  );
};

FnrRedirect.propTypes = {
  url: string,
  children: node,
  target: oneOf(["_self", "_blank"]),
  callback: func,
  fullWidth: bool,
};

export default FnrRedirect;
